/**
 * Browsing the library to pick one document — a file manager, not a dropdown.
 *
 * A `<select>` is fine for five things and useless for five hundred: it shows one row at a
 * time, sorts by nothing, and a UUID filename tells you nothing about what the document is.
 * This shows what a person actually recognises a scan by — its first page, its size, when
 * it arrived, which collection it sits in — and lets them look through collections the way
 * they would look through folders.
 *
 * Collections are the "places", deliberately. They are the only grouping in this product a
 * person curates on purpose, which is exactly what a folder is. Tags are ambient and are
 * not places; a sidebar full of them is a sidebar nobody reads.
 *
 * An overlay rather than an inline panel: the console's screens are pinned to one viewport
 * and a browser needs room. It traps nothing — Escape closes, the backdrop closes, and
 * nothing is destroyed by leaving.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { get, imageObjectUrl, upload } from "../lib/api";
import { lanes } from "../lib/throttle";
import { ago, bytes } from "../lib/format";
import type {
  CollectionSummary,
  DocumentDetail,
  DocumentList,
  DocumentSummary,
} from "../lib/types";
import { DataTable } from "./DataTable";
import { Button, Chip, Empty, Failed, Loading, STATUS_TONE } from "./ui";

/**
 * Thumbnails share three lanes, and no more.
 *
 * A page image is a server-side render of a PDF page and can be megabytes. Requesting one
 * per document meant forty at once: past the browser's ~6 connections per host they queue,
 * and the queue is shared — so the extract POST issued from the page behind this picker sat
 * unsent behind thirty page renders and the button hung forever. Bulk decoration must never
 * be able to starve a request a person is waiting on.
 */
const thumbnails = lanes(3);

/** The first page of a document, fetched only once its tile is actually on screen. */
function Thumb({ documentId }: { documentId: string }) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [visible, setVisible] = useState(false);
  const holder = useRef<HTMLSpanElement | null>(null);

  // Only fetch what someone can see. A picker scrolled to the top must not pay for the
  // thirty tiles below the fold.
  useEffect(() => {
    const node = holder.current;
    if (node === null) return;
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!visible) return;
    let live = true;
    let objectUrl: string | null = null;
    const abort = new AbortController();

    void thumbnails
      .run(() =>
        // Nothing in flight survives the picker closing: an abandoned render still holds a
        // connection, and the next thing the person does is the thing that needs it.
        live && !abort.signal.aborted
          ? imageObjectUrl(`/v1/documents/${documentId}/pages/0/image`, abort.signal)
          : Promise.reject(new Error("cancelled")),
      )
      .then((created) => {
        if (!live) {
          URL.revokeObjectURL(created);
          return;
        }
        objectUrl = created;
        setUrl(created);
      })
      // A document with no printed page answers 409, and a cancelled fetch throws too.
      // Neither is worth shouting about — the tile just has no picture.
      .catch(() => {
        if (live) setFailed(true);
      });

    return () => {
      live = false;
      abort.abort();
      if (objectUrl !== null) URL.revokeObjectURL(objectUrl);
    };
  }, [documentId, visible]);

  if (failed) {
    return (
      <span ref={holder} className="grid h-full w-full place-items-center bg-strong">
        <span className="font-mono text-[9px] tracking-[0.18em] text-faint uppercase">
          no page
        </span>
      </span>
    );
  }
  if (url === null) return <span ref={holder} className="block h-full w-full animate-pulse bg-strong" />;
  return (
    <span ref={holder} className="block h-full w-full">
      <img src={url} alt="" className="h-full w-full object-cover object-top" />
    </span>
  );
}

type Accepted = { document_id?: string; documents?: { document_id: string }[] };

export function DocumentPicker({
  open,
  onPick,
  onClose,
  onUploaded,
  title = "Choose a document",
  actionLabel,
  onAction,
}: {
  open: boolean;
  onPick: (document: DocumentSummary) => void;
  onClose: () => void;
  /** A file was just uploaded: its id, and no status yet — nothing has parsed it. */
  onUploaded?: (documentId: string) => void;
  title?: string;
  /**
   * The thing the person actually came to do, done here rather than back on the page.
   *
   * Choosing a document is not the goal; reading it is. Splitting those across a modal and
   * the page behind it means picking, being returned to a page that looks unchanged, and
   * hunting for the button that finishes the job — so the finishing action lives in the
   * footer beside the selection it applies to.
   */
  actionLabel?: string;
  onAction?: (documentId: string) => void;
}) {
  const [tab, setTab] = useState<"browse" | "upload">("browse");
  const [dragging, setDragging] = useState(false);
  const [sending, setSending] = useState(false);
  const [uploadError, setUploadError] = useState<unknown>(null);
  const [place, setPlace] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [view, setView] = useState<"list" | "icons">("icons");
  const [selected, setSelected] = useState<DocumentSummary | null>(null);
  /** A just-uploaded document, before the listing knows about it. */
  const [uploadedId, setUploadedId] = useState<string | null>(null);

  const collections = useQuery({
    queryKey: ["collections", "picker"],
    queryFn: () => get<{ collections: CollectionSummary[] }>("/v1/collections"),
    enabled: open,
  });

  const query = place === "all" ? "" : `&collection=${encodeURIComponent(place)}`;
  const listing = useQuery({
    queryKey: ["documents", "picker", place],
    queryFn: () => get<DocumentList>(`/v1/documents?limit=200${query}`),
    enabled: open,
  });

  const chosenId = selected?.id ?? uploadedId;
  const chosen = useQuery({
    queryKey: ["document", chosenId],
    enabled: open && chosenId !== null,
    queryFn: () => get<DocumentDetail>(`/v1/documents/${chosenId}`),
    refetchInterval: (query) =>
      query.state.data?.status === "succeeded" || query.state.data?.status === "failed"
        ? false
        : 2000,
  });
  // The listing already reports a status; the fetch is only needed while a fresh upload
  // is still being read.
  const chosenStatus = chosen.data?.status ?? selected?.status;
  const ready = chosenId !== null && chosenStatus === "succeeded";

  // Escape closes. A picker that can only be dismissed with the mouse is a trap.
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const rows = useMemo(() => {
    const all = listing.data?.documents ?? [];
    const needle = search.trim().toLowerCase();
    if (needle === "") return all;
    return all.filter(
      (document) =>
        (document.filename ?? "").toLowerCase().includes(needle) ||
        document.tags.some((tag) => tag.name.toLowerCase().includes(needle)) ||
        document.collections.some((one) => one.name.toLowerCase().includes(needle)),
    );
  }, [listing.data, search]);

  async function send(files: FileList | File[] | null) {
    const file = files?.[0];
    if (file === undefined || sending) return;
    setUploadError(null);
    setSending(true);
    try {
      const accepted = await upload<Accepted>("/v1/documents", [file]);
      const id = accepted.document_id ?? accepted.documents?.[0]?.document_id;
      if (id !== undefined) {
        setUploadedId(id);
        setSelected(null);
        setTab("browse");
        onUploaded?.(id);
      }
    } catch (error) {
      setUploadError(error);
    } finally {
      setSending(false);
    }
  }

  function Footer() {
    const name = selected?.filename ?? chosen.data?.filename ?? chosenId;
    const parsing = chosenId !== null && chosenStatus !== "succeeded" && chosenStatus !== "failed";
    return (
      <div className="flex shrink-0 flex-wrap items-center gap-3 border-t border-hair bg-ground-soft px-3.5 py-2.5">
        <span className="min-w-0 truncate font-mono text-[11px] text-faint">
          {chosenId === null ? (
            "nothing selected — click a document to select it"
          ) : (
            <>
              <span className="text-dim">selected </span>
              <span className="text-text">{name}</span>
            </>
          )}
        </span>
        {parsing && (
          <Chip tone="gold" dot>
            {chosen.data?.progress
              ? `parsing ${chosen.data.progress.pages_done}/${chosen.data.progress.pages_total}`
              : "parsing…"}
          </Chip>
        )}
        {chosenStatus === "failed" && <Chip tone="crit">parse failed</Chip>}
        {parsing && (
          <span className="text-[11.5px] text-dim">
            Reading the file. The form's structure comes from the parsed text, so this has to
            finish first.
          </span>
        )}
        <div className="ms-auto flex items-center gap-2">
          <Button variant="ghost" onClick={onClose}>
            cancel
          </Button>
          {onAction === undefined ? (
            <Button
              variant="primary"
              disabled={selected === null}
              onClick={() => selected !== null && onPick(selected)}
            >
              choose
            </Button>
          ) : (
            <Button
              variant="primary"
              disabled={!ready}
              onClick={() => chosenId !== null && onAction(chosenId)}
            >
              {actionLabel ?? "use this"}
            </Button>
          )}
        </div>
      </div>
    );
  }

  if (!open) return null;

  const here =
    place === "all"
      ? "all documents"
      : ((collections.data?.collections ?? []).find((one) => one.id === place)?.name ?? place);

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/70 p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        role="dialog"
        aria-label={title}
        onClick={(event) => event.stopPropagation()}
        className="flex h-[min(760px,88vh)] w-[min(1180px,94vw)] flex-col border border-hair-strong bg-surface"
      >
        {/* Title bar */}
        <header className="flex shrink-0 items-center gap-3 border-b border-hair bg-hair-soft px-4 py-2.5">
          <h3 className="font-mono text-[11px] font-bold tracking-[0.2em] uppercase">{title}</h3>
          <div className="flex border border-hair">
            {(["browse", "upload"] as const).map((which) => (
              <button
                key={which}
                type="button"
                onClick={() => setTab(which)}
                className={`px-3 py-1 font-mono text-[10px] tracking-[0.16em] uppercase ${
                  tab === which ? "bg-gold-surface text-on-gold" : "text-dim hover:text-gold"
                }`}
              >
                {which}
              </button>
            ))}
          </div>
          <div className="ml-auto flex items-center gap-2">
            <div className={`flex border border-hair ${tab === "browse" ? "" : "hidden"}`}>
              {(["icons", "list"] as const).map((mode) => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => setView(mode)}
                  className={`px-2.5 py-1 font-mono text-[10px] tracking-[0.16em] uppercase ${
                    view === mode ? "bg-gold-surface text-on-gold" : "text-dim hover:text-gold"
                  }`}
                >
                  {mode}
                </button>
              ))}
            </div>
            <Button variant="ghost" onClick={onClose}>
              close
            </Button>
          </div>
        </header>

        {tab === "upload" ? (
          <div
            className="flex min-h-0 flex-1 flex-col items-center justify-center gap-4 p-8"
            onDragOver={(event) => {
              event.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(event) => {
              event.preventDefault();
              setDragging(false);
              void send(event.dataTransfer.files);
            }}
          >
            <label
              className={`flex w-full max-w-2xl cursor-pointer flex-col items-center justify-center gap-3 border-2 border-dashed p-14 text-center transition-colors ${
                dragging ? "border-gold bg-gold-wash" : "border-hair-strong hover:border-gold"
              }`}
            >
              <span className="eyebrow">// drop a form here</span>
              <span className="text-[15px] font-bold">
                {sending ? "uploading…" : "Drag a file in, or click to choose one"}
              </span>
              <span className="max-w-md text-[12px] text-dim">
                pdf, docx, xlsx, pptx, images and more. It is parsed on arrival — extraction
                reads the parsed text, so a form is usable a moment after it lands.
              </span>
              <input
                type="file"
                aria-label="upload a form"
                className="hidden"
                disabled={sending}
                onChange={(event) => {
                  void send(event.target.files);
                  event.target.value = "";
                }}
              />
            </label>
            {uploadError !== null && <Failed error={uploadError} what="that upload" />}
            <p className="text-[12px] text-faint">
              Legacy <code>.doc</code> is not read — export it as PDF first.
            </p>
          </div>
        ) : (
        <div className="grid min-h-0 flex-1 grid-cols-[190px_minmax(0,1fr)] gap-px bg-hair">
          {/* Places */}
          <nav className="flex min-h-0 flex-col overflow-y-auto bg-ground-soft py-3">
            <span className="eyebrow px-4 pb-1.5">// places</span>
            <button
              type="button"
              onClick={() => setPlace("all")}
              className={`mx-2 flex items-center gap-2.5 px-2.5 py-1.5 text-left text-[13px] ${
                place === "all" ? "bg-gold-wash font-semibold text-gold" : "text-dim hover:text-text"
              }`}
            >
              all documents
            </button>
            {(collections.data?.collections ?? []).map((collection) => (
              <button
                key={collection.id}
                type="button"
                onClick={() => setPlace(collection.id)}
                className={`mx-2 flex items-center gap-2.5 px-2.5 py-1.5 text-left text-[13px] ${
                  place === collection.id
                    ? "bg-gold-wash font-semibold text-gold"
                    : "text-dim hover:text-text"
                }`}
              >
                <span className="truncate">{collection.name}</span>
                <span className="tnum ms-auto font-mono text-[10px] text-faint">
                  {collection.document_count}
                </span>
              </button>
            ))}
          </nav>

          {/* Browser */}
          <div className="flex min-h-0 flex-col bg-surface">
            <div className="flex shrink-0 flex-wrap items-center gap-3 border-b border-hair px-3.5 py-2">
              <span className="font-mono text-[11px] tracking-[0.1em] text-faint">
                documents <span className="text-dim">/</span>{" "}
                <span className="text-text">{here}</span>
              </span>
              <input
                aria-label="search documents"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="filter by name, tag or collection"
                className="ms-auto w-64 border border-hair bg-transparent px-2 py-1 font-mono text-[11.5px]"
              />
            </div>

            <div className="min-h-0 flex-1 overflow-auto">
              {listing.isError ? (
                <Failed error={listing.error} what="documents" />
              ) : listing.isPending ? (
                <Loading what="documents" />
              ) : rows.length === 0 ? (
                <Empty
                  title="nothing here"
                  hint="No document in this place matches. Try another collection, or clear the filter."
                />
              ) : view === "icons" ? (
                <ul className="grid grid-cols-[repeat(auto-fill,minmax(150px,1fr))] gap-3 p-3.5">
                  {rows.map((document) => {
                    const isSelected = selected?.id === document.id;
                    return (
                      <li key={document.id}>
                        <button
                          type="button"
                          onClick={() => setSelected(document)}
                          onDoubleClick={() => onPick(document)}
                          className={`flex w-full flex-col gap-2 border p-2 text-left ${
                            isSelected
                              ? "border-gold bg-gold-wash"
                              : "border-hair hover:border-hair-strong"
                          }`}
                        >
                          <span className="block h-28 w-full overflow-hidden border border-hair-soft bg-page-bg">
                            <Thumb documentId={document.id} />
                          </span>
                          <span className="truncate text-[12px]" title={document.filename ?? document.id}>
                            {document.filename ?? document.id}
                          </span>
                          <span className="flex items-center justify-between gap-2">
                            <Chip tone={STATUS_TONE[document.status] ?? "mut"}>
                              {document.status}
                            </Chip>
                            <span className="tnum font-mono text-[10px] text-faint">
                              {bytes(document.byte_size)}
                            </span>
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <DataTable
                  table="document-picker"
                  rows={rows}
                  rowKey={(row) => row.id}
                  onRowClick={(row) => setSelected(row)}
                  columns={[
                    {
                      key: "name",
                      header: "name",
                      width: 340,
                      render: (row) => row.filename ?? row.id,
                    },
                    {
                      key: "status",
                      header: "status",
                      width: 130,
                      render: (row) => (
                        <Chip tone={STATUS_TONE[row.status] ?? "mut"}>{row.status}</Chip>
                      ),
                    },
                    {
                      key: "collections",
                      header: "collections",
                      width: 200,
                      cellClass: "text-dim",
                      render: (row) =>
                        row.collections.length === 0
                          ? "—"
                          : row.collections.map((one) => one.name).join(", "),
                    },
                    {
                      key: "size",
                      header: "size",
                      width: 100,
                      align: "end",
                      cellClass: "tnum font-mono",
                      render: (row) => bytes(row.byte_size),
                    },
                    {
                      key: "when",
                      header: "added",
                      width: 120,
                      cellClass: "text-dim",
                      render: (row) => (row.created_at ? ago(row.created_at) : "—"),
                    },
                  ]}
                />
              )}
            </div>

            {/* Footer: what is chosen, and the only two ways out. */}
            <Footer />
          </div>
        </div>
        )}
      </div>
    </div>
  );
}
